import React from 'react';
import { TextBadge } from "../common";
import { format } from 'date-fns';
import enumChoice, { getEnumLabel } from '../../enum';
import NxtbnModal from '../../components/Modal';
import { useQuery, gql } from '@apollo/client';

const GET_PAYMENTS = gql`
  query GetPayments($alias: UUID!) {
    payments(first: 20, order_Alias: $alias) {
      edges {
        node {
          dbId
          gatewayName
          isSuccessful
          paidAt
          paymentAmount
          paymentMethod
          paymentPluginId
          paymentStatus
          transactionId
        }
      }
    }
  }
`;

interface PaymentTableProps {
  alias: string;
  isOpen: boolean;
  onClose: () => void;
}

const PaymentTable: React.FC<PaymentTableProps> = ({ alias, isOpen, onClose }) => {
  const { data, loading, error, refetch } = useQuery(GET_PAYMENTS, {
    variables: { alias },
    notifyOnNetworkStatusChange: true,
  });

  if (loading) return <p>Loading...</p>;
  if (error) return <p>Error: {error.message}</p>;

  const payments = data?.payments?.edges?.map((edge: { node: any }) => edge.node) || [];

  return (
    <NxtbnModal size='xl' isOpen={isOpen} onClose={onClose}>
      <div className="overflow-hidden p-6 bg-gradient-to-b from-gray-50 to-white">
        {/* Header Section */}
        <div className="mb-6">
          <h3 className="text-2xl font-bold text-gray-800 mb-2">Payment History</h3>
          <p className="text-sm text-gray-600">Track order transaction details and status</p>
        </div>

        {/* Table Container */}
        <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
          <table className="min-w-full">
            <thead>
              <tr className="bg-gray-100/75 backdrop-blur-sm border-b border-gray-200">
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 tracking-wider">Date</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 tracking-wider">Payment Method</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 tracking-wider">Amount</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 tracking-wider">Status</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 tracking-wider">Transaction ID</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {payments.length > 0 ? (
                payments.map((payment:any) => (
                  <tr
                    key={payment.dbId}
                    className="transition-colors hover:bg-gray-50/50 group"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-900">
                          {payment.paidAt ? format(new Date(payment.paidAt), 'PP') : 'N/A'}
                        </span>
                        <span className="text-xs text-gray-500">
                          {payment.paidAt ? format(new Date(payment.paidAt), 'p') : ''}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <TextBadge
                        size='sm'
                        value={payment.paymentMethod}
                        label={getEnumLabel(enumChoice.paymentMethods, payment.paymentMethod)}
                      />

                        {payment.gatewayName && (
                        <div className="mt-1 text-xs text-gray-500">
                          Gateway: {payment.gatewayName}
                        </div>
                        )}
                        {payment.paymentPluginId && (
                        <div className="mt-1 text-xs text-gray-500">
                          Plugin ID: {payment.paymentPluginId}
                        </div>
                        )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-semibold text-gray-900">
                        ${payment.paymentAmount}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <TextBadge
                        size='sm'
                        value={payment.paymentStatus}
                        label={getEnumLabel(enumChoice.paymentStatus, payment.paymentStatus)}
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-600 font-mono">
                        {payment.transactionId}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center justify-center text-gray-500">
                      <svg className="w-12 h-12 mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                      <p className="text-lg font-medium">No payment history available</p>
                      <p className="text-sm">Transactions will appear here once processed</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Footer Actions */}
        <div className="mt-6 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center rounded-lg px-6 py-2.5 bg-gray-800 text-white font-medium text-sm transition-colors hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-800 focus:ring-offset-2"
          >
            Close
          </button>
        </div>
      </div>
    </NxtbnModal>
  );
};

export default PaymentTable;
